import type { Model, ModelsResponse } from "../types";
import { COLD_START_RETRY, llmApiClient } from "./network";

export interface FetchModelsOptions {
  apiUrl: string;
  apiKey?: string;
}

/**
 * Fetch available models from an OpenAI-compatible API
 */
export async function fetchModels({
  apiUrl,
  apiKey,
}: FetchModelsOptions): Promise<Model[]> {
  const headers: Record<string, string> = {
    ...(apiKey && { Authorization: `Bearer ${apiKey}` }),
  };

  // Try LM Studio's extended endpoint first (has more metadata)
  // Retried: listing models is a read, and this is usually the FIRST call the
  // app makes — so it is the one that meets the LB still cold-starting, and a
  // failure here leaves the user with an empty model picker.
  let result = await llmApiClient.safeRequest<ModelsResponse>(
    `${apiUrl}/v1/lmstudio/models`,
    { headers, retry: COLD_START_RETRY }
  );

  // If extended endpoint fails, try standard OpenAI endpoint
  if (!result.ok) {
    result = await llmApiClient.safeRequest<ModelsResponse>(
      `${apiUrl}/v1/models`,
      { headers, retry: COLD_START_RETRY }
    );
  }

  if (!result.ok) {
    throw new Error(`Failed to fetch models: ${result.error.message}`);
  }

  const data = result.data;

  // Filter out embedding models and deduplicate by id
  const seen = new Set<string>();
  const models = (data.data || []).filter((model) => {
    if (seen.has(model.id)) return false;
    seen.add(model.id);

    if (model.type) {
      return model.type !== "embeddings";
    }
    // Fallback: filter by name patterns
    const name = model.id?.toLowerCase() || "";
    return !name.includes("embedding") && !name.includes("embed");
  });

  // Normalize capabilities: API may return an object like { reasoning: true }
  // instead of a string array — convert to string[] for consistent downstream usage
  return models.map((model) => {
    const caps = model.capabilities;
    if (caps && !Array.isArray(caps)) {
      const obj = caps as unknown as Record<string, unknown>;
      model.capabilities = Object.keys(obj).filter((key) => obj[key]);
    }
    return model;
  });
}
